import { useState, useEffect } from 'react';
import { getShopAccepted, updateOrderStatus } from '../utils/api';

const STATUS_FLOW = [
  { key: 'accepted', label: 'Accepted', icon: '✅', next: 'picked_up', nextLabel: 'Mark Picked Up' },
  { key: 'picked_up', label: 'Picked Up', icon: '🚗', next: 'in_progress', nextLabel: 'Start Cleaning' },
  { key: 'in_progress', label: 'In Progress', icon: '🫧', next: 'delivered', nextLabel: 'Mark Delivered' },
  { key: 'delivered', label: 'Delivered', icon: '🎉', next: null, nextLabel: null },
];

const STATUS_COLOR = {
  accepted: '#dbeafe',
  picked_up: '#ede9fe',
  in_progress: '#fef3c7',
  delivered: '#d1fae5',
  cancelled: '#fee2e2',
};

const STATUS_TEXT = {
  accepted: '#1e40af',
  picked_up: '#5b21b6',
  in_progress: '#92400e',
  delivered: '#065f46',
  cancelled: '#991b1b',
};

function ActiveOrderCard({ order, onUpdated }) {
  const [updating, setUpdating] = useState(false);
  const statusInfo = STATUS_FLOW.find(s => s.key === order.status);

  const handleNext = async () => {
    if (!statusInfo?.next) return;
    setUpdating(true);
    try {
      await updateOrderStatus(order.id, statusInfo.next);
      onUpdated?.();
    } catch (e) {
      alert(e.response?.data?.detail || 'Update failed');
    } finally { setUpdating(false); }
  };

  const stepIdx = STATUS_FLOW.findIndex(s => s.key === order.status);

  return (
    <div className="card">
      {/* Header */}
      <div style={{ padding: '1.25rem', background: STATUS_COLOR[order.status] || 'var(--bg)', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700, color: STATUS_TEXT[order.status] }}>
            {statusInfo?.icon} {statusInfo?.label || order.status}
          </div>
          <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: 2 }}>
            Order #{order.id?.slice(-8)}
          </div>
        </div>
        <div style={{ fontSize: '0.78rem', color: STATUS_TEXT[order.status], opacity: 0.8 }}>
          {new Date(order.updated_at || order.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>

      <div style={{ padding: '1.25rem' }}>
        {/* Progress dots */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 16 }}>
          {STATUS_FLOW.map((s, i) => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', flex: i < STATUS_FLOW.length - 1 ? 1 : 0 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: i <= stepIdx ? 'var(--primary)' : 'var(--border)',
                color: i <= stepIdx ? 'white' : 'var(--text-muted)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.75rem', fontWeight: 700, transition: 'all 0.3s'
              }}>
                {i < stepIdx ? '✓' : i + 1}
              </div>
              {i < STATUS_FLOW.length - 1 && (
                <div style={{ flex: 1, height: 3, background: i < stepIdx ? 'var(--primary)' : 'var(--border)', transition: 'background 0.3s' }} />
              )}
            </div>
          ))}
        </div>

        {/* Customer info */}
        <div style={{ padding: '10px 12px', background: 'var(--bg)', borderRadius: 10, marginBottom: 12 }}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{order.customer?.name || 'Customer'}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{order.customer?.phone}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: 4 }}>
            📍 {order.customer_address}
          </div>
        </div>

        {/* Details row */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div style={{ flex: 1, padding: '8px 12px', background: 'var(--bg)', borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>{order.clothes_count}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Items</div>
          </div>
          <div style={{ flex: 2, padding: '8px 12px', background: 'var(--bg)', borderRadius: 8 }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Services</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {order.services?.map(s => (
                <span key={s} className="order-service-tag">{s.replace('_', ' ')}</span>
              ))}
            </div>
          </div>
        </div>

        {order.notes && (
          <div style={{ padding: '8px 12px', background: 'var(--primary-light)', borderRadius: 8, fontSize: '0.83rem', marginBottom: 12 }}>
            💬 {order.notes}
          </div>
        )}

        {/* Next action button */}
        {statusInfo?.next && (
          <button
            className="btn btn-primary btn-full"
            onClick={handleNext}
            disabled={updating}
            style={{ padding: '12px' }}
          >
            {updating ? 'Updating...' : `${statusInfo.nextLabel} →`}
          </button>
        )}
        {order.status === 'delivered' && (
          <div style={{ textAlign: 'center', padding: '10px', background: '#d1fae5', borderRadius: 10, color: '#065f46', fontWeight: 600, fontSize: '0.9rem' }}>
            🎉 Order Complete!
          </div>
        )}
      </div>
    </div>
  );
}

export default function OwnerOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');

  const load = async () => {
    try {
      const res = await getShopAccepted();
      setOrders(res.data);
    } catch (e) {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const activeStatuses = ['accepted', 'picked_up', 'in_progress'];
  const filtered = filter === 'active'
    ? orders.filter(o => activeStatuses.includes(o.status))
    : orders.filter(o => o.status === 'delivered');

  const activeCount = orders.filter(o => activeStatuses.includes(o.status)).length;

  return (
    <div className="page">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1>My Orders</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>{activeCount} active order{activeCount !== 1 ? 's' : ''}</p>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Accepted', count: orders.filter(o => o.status === 'accepted').length, color: '#dbeafe', text: '#1e40af' },
          { label: 'Picked Up', count: orders.filter(o => o.status === 'picked_up').length, color: '#ede9fe', text: '#5b21b6' },
          { label: 'Cleaning', count: orders.filter(o => o.status === 'in_progress').length, color: '#fef3c7', text: '#92400e' },
          { label: 'Delivered', count: orders.filter(o => o.status === 'delivered').length, color: '#d1fae5', text: '#065f46' },
        ].map(stat => (
          <div key={stat.label} style={{ padding: '12px 16px', background: stat.color, borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: stat.text, fontFamily: 'poppins, sans-serif' }}>{stat.count}</div>
            <div style={{ fontSize: '0.78rem', color: stat.text, opacity: 0.8 }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        <button className={`tab ${filter === 'active' ? 'active' : ''}`} onClick={() => setFilter('active')}>
          Active ({activeCount})
        </button>
        <button className={`tab ${filter === 'delivered' ? 'active' : ''}`} onClick={() => setFilter('delivered')}>
          Completed
        </button>
      </div>

      {loading && <div className="spinner" />}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>
            {filter === 'active' ? '🎉' : '📦'}
          </div>
          <p>{filter === 'active' ? 'No active orders right now' : 'No completed orders yet'}</p>
        </div>
      )}

      <div className="grid-auto">
        {filtered.map(order => (
          <ActiveOrderCard key={order.id} order={order} onUpdated={load} />
        ))}
      </div>
    </div>
  );
}
